import { useSuspenseQuery } from '@tanstack/react-query';
import { getMany, keys } from 'idb-keyval';
import * as React from 'react';

import { Logger } from '@gitbook/util-debug';

const logger = Logger('app:legacyStorage');

const LEGACY_KEY_PREFIX = '@recoil/';

/**
 * Set once the migration below has run, so it costs a single synchronous `localStorage` read on
 * every later boot instead of an IndexedDB round-trip.
 */
const MIGRATION_MARKER_KEY = '@gitbook/legacy-state-migrated';

/**
 * Legacy atoms deliberately left behind: they either carry their own migration (the basic editor
 * reads its legacy IndexedDB drafts directly) or hold documents far too large to move into
 * `localStorage`.
 */
const SKIPPED_LEGACY_KEYS = new Set(['basicEditorValuesAtom', 'glossary:languagesToDisplay']);

/**
 * Legacy atoms that persisted a `Set` through a `JSON.stringify(Array.from(value))` serializer.
 * Their stored value is already the exact JSON string the replacement atom expects, so encoding it
 * again would double-escape it.
 */
const PRE_ENCODED_LEGACY_KEYS = new Set([
    'ignoredSiteFeatureCardKeys',
    'ignoredSiteTaskIds',
    'sidebarSitesExpanded',
    'sidebarSitesTouched',
    'sidebarSpacesExpanded',
]);

const LEGACY_STATE_MIGRATION_QUERY_KEY = ['legacy-state-migration'] as const;

/**
 * A blocked IndexedDB can leave a request pending indefinitely, and the whole app is suspended on
 * this one. Bound the wait so a boot always makes progress.
 */
const INDEXEDDB_TIMEOUT = 5_000;

/**
 * Move the state persisted by the Recoil-era atoms onto the keys their Jotai replacements read.
 *
 * Recoil stored every atom under `@recoil/<node key>` — in IndexedDB by default, in `localStorage`
 * for the few atoms that opted into it — while `atomWithStorage` reads the bare node key from
 * `localStorage`. The node keys themselves were kept identical through the migration (including the
 * `<key>__<JSON param>` form Recoil gave atom families), so realigning the two is a prefix strip.
 *
 * Never rejects: a browser that denies storage should still boot the app.
 */
export async function migrateLegacyPersistedState(): Promise<void> {
    try {
        migrateLegacyLocalStorage();
        await withTimeout(migrateLegacyIndexedDB(), INDEXEDDB_TIMEOUT);
    } catch (error) {
        // Deliberately leave the marker unset. IndexedDB holds the bulk of what there is to move,
        // so orphaning it permanently because a transaction failed once is worse than paying for
        // another attempt on the next boot.
        logger.error('failed to migrate legacy persisted state', error);
        return;
    }

    try {
        window.localStorage.setItem(MIGRATION_MARKER_KEY, 'true');
    } catch {
        // localStorage can be unavailable in privacy-restricted contexts.
    }
}

/**
 * Run {@link migrateLegacyPersistedState} before rendering anything that reads persisted state.
 *
 * The gate matters as much as the migration: `atomWithStorage` re-reads its key when the atom is
 * first subscribed to, so as long as no consumer has mounted yet, every atom below picks the
 * migrated value up on its own. Suspending here is what guarantees that.
 */
export function PersistedStateMigrationProvider(props: {
    children: React.ReactNode;
}): React.ReactElement {
    // The marker cannot change while the app is running, so reading it once keeps the common case
    // (already migrated) free of a suspension and its loading flash.
    const [isPending] = React.useState(isLegacyStateMigrationPending);

    if (!isPending) {
        return <>{props.children}</>;
    }

    return <MigrateLegacyPersistedState>{props.children}</MigrateLegacyPersistedState>;
}

function MigrateLegacyPersistedState(props: { children: React.ReactNode }): React.ReactElement {
    useSuspenseQuery({
        queryKey: LEGACY_STATE_MIGRATION_QUERY_KEY,
        queryFn: async () => {
            await migrateLegacyPersistedState();
            return true;
        },
        gcTime: Number.POSITIVE_INFINITY,
        staleTime: Number.POSITIVE_INFINITY,
        meta: { persist: false },
        networkMode: 'always',
        retry: false,
    });

    return <>{props.children}</>;
}

function isLegacyStateMigrationPending(): boolean {
    try {
        return window.localStorage.getItem(MIGRATION_MARKER_KEY) === null;
    } catch {
        // Without localStorage there is nothing to migrate into.
        return false;
    }
}

/**
 * Rename the `@recoil/`-prefixed keys that were already persisted in localStorage.
 */
function migrateLegacyLocalStorage(): void {
    const storage = window.localStorage;

    // Collected upfront: writing to localStorage while walking it by index shifts the later keys.
    const legacyKeys: string[] = [];
    for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index);
        if (key?.startsWith(LEGACY_KEY_PREFIX)) {
            legacyKeys.push(key);
        }
    }

    for (const legacyKey of legacyKeys) {
        const value = storage.getItem(legacyKey);
        if (value !== null) {
            writeMigratedValue(legacyKey.slice(LEGACY_KEY_PREFIX.length), value);
        }
    }
}

/**
 * Move the `@recoil/`-prefixed entries of the default IndexedDB store into localStorage.
 */
async function migrateLegacyIndexedDB(): Promise<void> {
    const storedKeys = await keys<IDBValidKey>();

    // Read the values of the keys we will actually write, and only those. A skipped basic editor
    // draft holds a whole document, and materializing every one of them just to discard them would
    // stall the boot this migration suspends.
    const legacyKeys = storedKeys.filter(
        (key): key is string =>
            typeof key === 'string' &&
            key.startsWith(LEGACY_KEY_PREFIX) &&
            !isSkippedLegacyKey(key.slice(LEGACY_KEY_PREFIX.length))
    );
    if (legacyKeys.length === 0) {
        return;
    }

    const values = await getMany<unknown>(legacyKeys);

    legacyKeys.forEach((legacyKey, index) => {
        const value = values[index];
        if (value === undefined) {
            return;
        }

        const key = legacyKey.slice(LEGACY_KEY_PREFIX.length);
        const isPreEncoded = PRE_ENCODED_LEGACY_KEYS.has(getLegacyBaseKey(key));
        writeMigratedValue(
            key,
            isPreEncoded && typeof value === 'string' ? value : JSON.stringify(value)
        );
    });
}

/**
 * Write a migrated value, leaving both an existing value and the legacy entry untouched: the
 * migration must stay idempotent, and keeping the source around means a rollback still finds it.
 */
function writeMigratedValue(key: string, value: string): void {
    if (isSkippedLegacyKey(key)) {
        return;
    }

    try {
        if (window.localStorage.getItem(key) === null) {
            window.localStorage.setItem(key, value);
        }
    } catch (error) {
        // A single oversized value (or a full quota) must not abort the rest of the migration.
        logger.error(`failed to migrate legacy state for "${key}"`, error);
    }
}

function isSkippedLegacyKey(key: string): boolean {
    return SKIPPED_LEGACY_KEYS.has(getLegacyBaseKey(key));
}

/**
 * Strip the serialized parameter Recoil appended to the keys of an atom family, so families can be
 * classified by the atom they came from.
 */
function getLegacyBaseKey(key: string): string {
    const separatorIndex = key.indexOf('__');
    return separatorIndex === -1 ? key : key.slice(0, separatorIndex);
}

function withTimeout<T>(promise: Promise<T>, timeout: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(`timed out after ${timeout}ms`)), timeout);
        promise.then(resolve, reject).finally(() => clearTimeout(timer));
    });
}
